import type { WebsiteAgentSuggestion } from "~/lib/marketplace/website-suggestions";
import {
  applyOrganizationLayoutToSelection,
  resolveHireOrganizationLayout,
} from "~/lib/marketplace/hire-organization-layout";
import {
  formatHirePrice,
  type MarketplaceSchedulingIntegrationId,
} from "~/lib/marketplace/hire-pricing";
import type { HireTeamHandoffCtaCluster } from "~/lib/marketplace/hire-team-handoff-ctas";
import { normalizeHireTeamHandoffCtaClusters } from "~/lib/marketplace/hire-team-handoff-ctas";
import { DEFAULT_TEAM_HANDOFF_CTAS } from "~/lib/onboarding/marketplace-onboarding-defaults";
import {
  buildDefaultHireLocationDetail,
  padHireLocationDetails,
} from "~/lib/marketplace/hire-location-utils";
import { padHireDepartmentDetails } from "~/lib/marketplace/hire-department-utils";
import type { IndustryVertical } from "~/lib/utils/industry-profile";

/** Monthly skill add-ons (cents). Live Chat is always free when enabled. */
export const SKILL_LIVE_CHAT_MONTHLY_CENTS = 0;
export const SKILL_TEXT_MESSAGES_MONTHLY_CENTS = 1000;
export const SKILL_EMAIL_MONTHLY_CENTS = 1500;
export const SKILL_BROWSER_CALLS_MONTHLY_CENTS = 0;
export const SKILL_VIDEO_CALLS_MONTHLY_CENTS = 1000;
export const SKILL_PHONE_CALLS_MONTHLY_CENTS = 3000;
export const SKILL_WELCOME_VIDEO_MONTHLY_CENTS = 5000;
export const SKILL_MESSENGER_MONTHLY_CENTS = 0;
export const SKILL_WHATSAPP_MONTHLY_CENTS = 0;

/** Employer details (cents per unit per month). */
export const RECOMMENDATIONS_TEAM_MEMBER_MONTHLY_CENTS = 1500;
export const RECOMMENDATIONS_LOCATION_MONTHLY_CENTS = 1000;
export const RECOMMENDATIONS_DEPARTMENT_MONTHLY_CENTS = 1000;

/** Usage-based pricing (cents per unit). */
export const USAGE_CHAT_CONVERSATION_CENTS = 10;
export const USAGE_SMS_CENTS = 10;
export const USAGE_PHONE_MINUTE_CENTS = 50;
export const USAGE_VIDEO_MINUTE_CENTS = 25;
export const USAGE_WELCOME_VIDEO_MINUTE_CENTS = 25;

/** Included free tiers (not billed). */
export const INCLUDED_FREE_CHAT_CONVERSATIONS = 10;
export const INCLUDED_FREE_SMS = 100;
export const INCLUDED_FREE_PHONE_MINUTES = 20;
export const INCLUDED_FREE_VIDEO_MINUTES = 20;
export const INCLUDED_FREE_WELCOME_VIDEO_MINUTES = 20;
export const INCLUDED_FREE_LOCATIONS = 1;
export const INCLUDED_FREE_DEPARTMENTS = 1;
export const INCLUDED_FREE_TEAM_MEMBERS = 1;

/** Agent tools (cents per month). */
export const TOOL_GOOGLE_CALENDAR_MONTHLY_CENTS = 2000;
export const TOOL_CALENDLY_MONTHLY_CENTS = 2000;
export const TOOL_ZOOM_MONTHLY_CENTS = 2000;
export const TOOL_EMR_MONTHLY_CENTS = 5000;

/** Agent training (cents per month). */
export const AGENT_TRAINING_BASIC_MONTHLY_CENTS = 0;
export const AGENT_TRAINING_ADVANCED_MONTHLY_CENTS = 3500;

export type MarketplaceEmrIntegrationId = Extract<
  MarketplaceSchedulingIntegrationId,
  | "none"
  | "juvonno"
  | "dentrix"
  | "dentrix_ascend"
  | "open_dental"
  | "eaglesoft"
  | "curve"
  | "denticon"
>;

export type MarketplaceAppointmentsIntegrationId = Extract<
  MarketplaceSchedulingIntegrationId,
  "none" | "google_calendar" | "calendly" | "zoom"
>;

const EMR_INTEGRATION_ID_SET = new Set<MarketplaceSchedulingIntegrationId>([
  "juvonno",
  "dentrix",
  "dentrix_ascend",
  "open_dental",
  "eaglesoft",
  "curve",
  "denticon",
]);

export function isEmrIntegrationId(
  id: MarketplaceSchedulingIntegrationId,
): id is Exclude<MarketplaceEmrIntegrationId, "none"> {
  return EMR_INTEGRATION_ID_SET.has(id);
}

export const HIRE_RECOMMENDATIONS_SESSION_KEY = "egpt-hire-recommendations-selection";

export type HireLocationDetail = {
  name: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  phone?: string;
  businessHours?: string;
  /** Email of the lead team member assigned to this location */
  leadTeamMemberEmail?: string;
  /** Emails of additional team members assigned to this location */
  otherTeamMemberEmails?: string[];
};

export type HireExtraTeamMember = {
  name: string;
  email: string;
  title?: string;
  phone?: string;
  imageUrl?: string;
  /** Department this person should lead / oversee (schools, nonprofits). */
  departmentName?: string;
  /** Location this person should lead (multi-location businesses). */
  locationName?: string;
};

export type HireDepartmentDetail = {
  name: string;
  /** Email of the lead team member assigned to this department */
  leadTeamMemberEmail?: string;
  /** Emails of additional team members assigned to this department */
  otherTeamMemberEmails?: string[];
};

export type { HireTeamHandoffCtaCluster } from "~/lib/marketplace/hire-team-handoff-ctas";

export type HireRecommendationsSelection = {
  liveChat: boolean;
  textMessages: boolean;
  email: boolean;
  browserCalls: boolean;
  videoCalls: boolean;
  phoneCalls: boolean;
  welcomeVideo: boolean;
  messenger: boolean;
  whatsapp: boolean;
  dedicatedEmail: boolean;
  dedicatedPhone: boolean;
  appointmentsEnabled: boolean;
  schedulingIntegrationId: MarketplaceSchedulingIntegrationId;
  emrIntegrationEnabled: boolean;
  emrIntegrationId: MarketplaceEmrIntegrationId;
  agentTrainingBasic: boolean;
  agentTrainingAdvanced: boolean;
  chatConversations: number;
  smsCount: number;
  phoneCallMinutes: number;
  videoCallMinutes: number;
  welcomeVideoMinutes: number;
  locationCount: number;
  locationList: string[];
  locationDetails: HireLocationDetail[];
  departmentCount: number;
  departmentDetails: HireDepartmentDetail[];
  teamMemberCount: number;
  teamMembersLabel: string;
  extraTeamMembers: HireExtraTeamMember[];
  teamHandoffEnabled: boolean;
  teamHandoffCtaClusters: HireTeamHandoffCtaCluster[];
};

export function schedulingIntegrationPriceCents(
  id: MarketplaceSchedulingIntegrationId,
): number {
  switch (id) {
    case "google_calendar":
      return TOOL_GOOGLE_CALENDAR_MONTHLY_CENTS;
    case "calendly":
      return TOOL_CALENDLY_MONTHLY_CENTS;
    case "zoom":
      return TOOL_ZOOM_MONTHLY_CENTS;
    default:
      return 0;
  }
}

export function emrIntegrationPriceCents(id: MarketplaceEmrIntegrationId): number {
  if (id === "none") return 0;
  return TOOL_EMR_MONTHLY_CENTS;
}

export function billableLocationCount(count: number): number {
  return Math.max(0, count - INCLUDED_FREE_LOCATIONS);
}

export function billableDepartmentCount(count: number): number {
  return Math.max(0, count - INCLUDED_FREE_DEPARTMENTS);
}

export function billableTeamMemberCount(count: number): number {
  return Math.max(0, count - INCLUDED_FREE_TEAM_MEMBERS);
}

export function billableChatConversations(
  _selection: HireRecommendationsSelection,
): number {
  return 0;
}

export function billableSmsMessages(selection: HireRecommendationsSelection): number {
  if (!selection.textMessages) return 0;
  return Math.max(0, selection.smsCount - INCLUDED_FREE_SMS);
}

export function billablePhoneMinutes(selection: HireRecommendationsSelection): number {
  if (!selection.phoneCalls) return 0;
  return Math.max(0, selection.phoneCallMinutes - INCLUDED_FREE_PHONE_MINUTES);
}

export function billableVideoMinutes(selection: HireRecommendationsSelection): number {
  if (!selection.videoCalls) return 0;
  return Math.max(0, selection.videoCallMinutes - INCLUDED_FREE_VIDEO_MINUTES);
}

export function billableWelcomeVideoMinutes(
  selection: HireRecommendationsSelection,
): number {
  if (!selection.welcomeVideo) return 0;
  return Math.max(0, selection.welcomeVideoMinutes - INCLUDED_FREE_WELCOME_VIDEO_MINUTES);
}

export function computeLocationCostCents(count: number): number {
  return billableLocationCount(count) * RECOMMENDATIONS_LOCATION_MONTHLY_CENTS;
}

export function computeDepartmentCostCents(count: number): number {
  return billableDepartmentCount(count) * RECOMMENDATIONS_DEPARTMENT_MONTHLY_CENTS;
}

export function computeTeamMemberCostCents(count: number): number {
  return billableTeamMemberCount(count) * RECOMMENDATIONS_TEAM_MEMBER_MONTHLY_CENTS;
}

export function computeUsageChatCostCents(selection: HireRecommendationsSelection): number {
  return billableChatConversations(selection) * USAGE_CHAT_CONVERSATION_CENTS;
}

export function computeUsageSmsCostCents(selection: HireRecommendationsSelection): number {
  return billableSmsMessages(selection) * USAGE_SMS_CENTS;
}

export function computeUsagePhoneCostCents(selection: HireRecommendationsSelection): number {
  return billablePhoneMinutes(selection) * USAGE_PHONE_MINUTE_CENTS;
}

export function computeUsageVideoCostCents(selection: HireRecommendationsSelection): number {
  return billableVideoMinutes(selection) * USAGE_VIDEO_MINUTE_CENTS;
}

export function computeUsageWelcomeVideoCostCents(
  selection: HireRecommendationsSelection,
): number {
  return billableWelcomeVideoMinutes(selection) * USAGE_WELCOME_VIDEO_MINUTE_CENTS;
}

export function computeRecommendationsTotalCents(
  selection: HireRecommendationsSelection,
): number {
  let total = 0;

  if (selection.liveChat) total += SKILL_LIVE_CHAT_MONTHLY_CENTS;
  if (selection.textMessages) total += SKILL_TEXT_MESSAGES_MONTHLY_CENTS;
  if (selection.email) total += SKILL_EMAIL_MONTHLY_CENTS;
  if (selection.browserCalls) total += SKILL_BROWSER_CALLS_MONTHLY_CENTS;
  if (selection.videoCalls) total += SKILL_VIDEO_CALLS_MONTHLY_CENTS;
  if (selection.phoneCalls) total += SKILL_PHONE_CALLS_MONTHLY_CENTS;
  if (selection.welcomeVideo) total += SKILL_WELCOME_VIDEO_MONTHLY_CENTS;
  if (selection.messenger) total += SKILL_MESSENGER_MONTHLY_CENTS;
  if (selection.whatsapp) total += SKILL_WHATSAPP_MONTHLY_CENTS;

  if (selection.appointmentsEnabled && selection.schedulingIntegrationId !== "none") {
    total += schedulingIntegrationPriceCents(selection.schedulingIntegrationId);
  }

  if (selection.emrIntegrationEnabled && selection.emrIntegrationId !== "none") {
    total += emrIntegrationPriceCents(selection.emrIntegrationId);
  }

  if (selection.agentTrainingAdvanced) {
    total += AGENT_TRAINING_ADVANCED_MONTHLY_CENTS;
  }

  total += computeUsageChatCostCents(selection);
  total += computeUsageSmsCostCents(selection);
  total += computeUsagePhoneCostCents(selection);
  total += computeUsageVideoCostCents(selection);
  total += computeUsageWelcomeVideoCostCents(selection);

  total += computeLocationCostCents(selection.locationCount);
  total += computeDepartmentCostCents(selection.departmentCount);
  total += computeTeamMemberCostCents(selection.teamMemberCount);

  return total;
}

export type PricingLineItem = { label: string; amountCents: number };

/** Line items for deploy/checkout summary from current recommendation toggles. */
export function summarizeRecommendationsPricing(
  selection: HireRecommendationsSelection,
): PricingLineItem[] {
  const lines: PricingLineItem[] = [];

  if (selection.textMessages) {
    lines.push({ label: "Text Messages", amountCents: SKILL_TEXT_MESSAGES_MONTHLY_CENTS });
  }
  if (selection.email) {
    lines.push({ label: "Email", amountCents: SKILL_EMAIL_MONTHLY_CENTS });
  }
  if (selection.welcomeVideo) {
    lines.push({ label: "Welcome Video", amountCents: SKILL_WELCOME_VIDEO_MONTHLY_CENTS });
  }
  if (selection.videoCalls) {
    lines.push({ label: "Video Calls", amountCents: SKILL_VIDEO_CALLS_MONTHLY_CENTS });
  }
  if (selection.phoneCalls) {
    lines.push({ label: "Phone Calls", amountCents: SKILL_PHONE_CALLS_MONTHLY_CENTS });
  }

  if (selection.appointmentsEnabled && selection.schedulingIntegrationId !== "none") {
    const appt = RECOMMENDATIONS_APPOINTMENTS_OPTIONS.find(
      (o) => o.id === selection.schedulingIntegrationId,
    );
    lines.push({
      label: appt ? `${appt.label} scheduling` : "Appointments scheduling",
      amountCents: schedulingIntegrationPriceCents(selection.schedulingIntegrationId),
    });
  }

  if (selection.emrIntegrationEnabled && selection.emrIntegrationId !== "none") {
    const emr = RECOMMENDATIONS_EMR_OPTIONS.find((o) => o.id === selection.emrIntegrationId);
    lines.push({
      label: emr ? `${emr.label} EMR` : "EMR integration",
      amountCents: emrIntegrationPriceCents(selection.emrIntegrationId),
    });
  }

  if (selection.agentTrainingAdvanced) {
    lines.push({
      label: "Advanced agent training",
      amountCents: AGENT_TRAINING_ADVANCED_MONTHLY_CENTS,
    });
  }

  const locCost = computeLocationCostCents(selection.locationCount);
  if (locCost > 0) {
    lines.push({
      label: `Additional locations (${billableLocationCount(selection.locationCount)})`,
      amountCents: locCost,
    });
  }

  const deptCost = computeDepartmentCostCents(selection.departmentCount);
  if (deptCost > 0) {
    lines.push({
      label: `Additional departments (${billableDepartmentCount(selection.departmentCount)})`,
      amountCents: deptCost,
    });
  }

  const teamCost = computeTeamMemberCostCents(selection.teamMemberCount);
  if (teamCost > 0) {
    lines.push({
      label: `Additional team members (${billableTeamMemberCount(selection.teamMemberCount)})`,
      amountCents: teamCost,
    });
  }

  const smsUsage = computeUsageSmsCostCents(selection);
  if (smsUsage > 0) {
    lines.push({ label: "SMS (over included)", amountCents: smsUsage });
  }

  const phoneUsage = computeUsagePhoneCostCents(selection);
  if (phoneUsage > 0) {
    lines.push({ label: "Phone minutes (over included)", amountCents: phoneUsage });
  }

  const videoUsage = computeUsageVideoCostCents(selection);
  if (videoUsage > 0) {
    lines.push({ label: "Video minutes (over included)", amountCents: videoUsage });
  }

  const welcomeVideoUsage = computeUsageWelcomeVideoCostCents(selection);
  if (welcomeVideoUsage > 0) {
    lines.push({
      label: "Welcome video minutes (over included)",
      amountCents: welcomeVideoUsage,
    });
  }

  return lines.filter((line) => line.amountCents > 0);
}

const TOOL_NAME_TO_INTEGRATION: Record<string, MarketplaceSchedulingIntegrationId> = {
  "google calendar": "google_calendar",
  calendly: "calendly",
  juvonno: "juvonno",
  dentrix: "dentrix",
  "dentrix ascend": "dentrix_ascend",
  "open dental": "open_dental",
  eaglesoft: "eaglesoft",
  curve: "curve",
  denticon: "denticon",
  nexhealth: "juvonno",
};

function mapToolNameToIntegration(name: string): MarketplaceSchedulingIntegrationId | null {
  const key = name.trim().toLowerCase();
  return TOOL_NAME_TO_INTEGRATION[key] ?? null;
}

function skillEnabled(skills: string[], label: string): boolean {
  return skills.some((s) => s.toLowerCase().includes(label.toLowerCase()));
}

export function defaultRecommendationsFromSuggestion(
  suggestion: WebsiteAgentSuggestion,
): HireRecommendationsSelection {
  const rec = suggestion.recommendations;
  const locationCount = Math.max(1, rec.locationList.length || 1);
  const teamMemberCount = 1;

  const rawLocationDetails =
    rec.locationDetails ?? locationListToDetails(rec.locationList);
  const locationDetails = padHireLocationDetails(
    rawLocationDetails,
    locationCount,
    suggestion.businessName,
  );

  const departmentDefaults = defaultHireDepartmentsForVertical(
    suggestion.vertical,
    `${suggestion.businessName} ${suggestion.summary}`,
  );

  const appointmentTool =
    rec.appointmentsScheduling
      .map(mapToolNameToIntegration)
      .find((id): id is MarketplaceSchedulingIntegrationId => id != null) ??
    (rec.emrIntegration ? mapToolNameToIntegration(rec.emrIntegration) : null) ??
    rec.tools?.map(mapToolNameToIntegration).find((id): id is MarketplaceSchedulingIntegrationId => id != null) ??
    "none";

  const isEmr = appointmentTool !== "none" && isEmrIntegrationId(appointmentTool);

  const baseSelection: HireRecommendationsSelection = {
    liveChat: true,
    textMessages: skillEnabled(rec.skills, "text") || skillEnabled(rec.skills, "sms"),
    email: skillEnabled(rec.skills, "email"),
    browserCalls: true,
    videoCalls: skillEnabled(rec.skills, "video"),
    phoneCalls: skillEnabled(rec.skills, "phone"),
    welcomeVideo: skillEnabled(rec.skills, "welcome video"),
    messenger: true,
    whatsapp: true,
    dedicatedEmail: false,
    dedicatedPhone: false,
    appointmentsEnabled:
      !isEmr &&
      (appointmentTool !== "none" || skillEnabled(rec.skills, "appointment")),
    schedulingIntegrationId: isEmr ? "none" : appointmentTool,
    emrIntegrationEnabled: isEmr,
    emrIntegrationId: isEmr ? appointmentTool : "none",
    agentTrainingBasic: true,
    agentTrainingAdvanced: false,
    chatConversations: INCLUDED_FREE_CHAT_CONVERSATIONS,
    smsCount:
      skillEnabled(rec.skills, "text") || skillEnabled(rec.skills, "sms")
        ? INCLUDED_FREE_SMS
        : 0,
    phoneCallMinutes: skillEnabled(rec.skills, "phone") ? INCLUDED_FREE_PHONE_MINUTES : 0,
    videoCallMinutes: skillEnabled(rec.skills, "video") ? INCLUDED_FREE_VIDEO_MINUTES : 0,
    welcomeVideoMinutes: skillEnabled(rec.skills, "welcome video")
      ? INCLUDED_FREE_WELCOME_VIDEO_MINUTES
      : 0,
    locationCount,
    locationList: locationDetails.map((loc) => loc.name),
    locationDetails,
    departmentCount: departmentDefaults.length,
    departmentDetails: departmentDefaults,
    teamMemberCount,
    teamMembersLabel: rec.teamMembers,
    extraTeamMembers: [],
    teamHandoffEnabled: true,
    teamHandoffCtaClusters: normalizeHireTeamHandoffCtaClusters(
      rec.teamHandoffCtaClusters,
      DEFAULT_TEAM_HANDOFF_CTAS,
    ),
  };

  const orgLayout = resolveHireOrganizationLayout(
    suggestion.vertical,
    `${suggestion.businessName} ${suggestion.summary}`,
  );

  return applyOrganizationLayoutToSelection(baseSelection, orgLayout, {
    vertical: suggestion.vertical,
    businessName: suggestion.businessName,
    contentHint: `${suggestion.businessName} ${suggestion.summary}`,
  });
}

export function computeEffectiveTeamMemberCount(args: {
  employerEmail: string;
  leadEmail?: string;
  extraCount?: number;
}): number {
  const employer = args.employerEmail.trim().toLowerCase();
  const lead = args.leadEmail?.trim().toLowerCase();
  let count = 1;
  if (lead && lead !== employer) count += 1;
  return count + (args.extraCount ?? 0);
}

export function locationListToDetails(locationList: string[]): HireLocationDetail[] {
  return locationList.map((label) => ({ name: label }));
}

/** Church / nonprofit cues — must win over bare "school" (e.g. Summit Christian School on a church site). */
export function looksChurchOrNonprofitContent(
  contentHint?: string,
  vertical?: IndustryVertical,
): boolean {
  if (vertical === "nonprofit") return true;
  const lower = (contentHint ?? "").toLowerCase();
  return /\b(church|parish|ministry|ministries|worship|congregation|diocese|faith community|synagogue|mosque|temple|nonprofit|non-profit|not-for-profit)\b/.test(
    lower,
  );
}

/**
 * Pull department names from Organization Profile / Extended Knowledge.
 * Prefer ## Service Offerings (and Ministries / Departments) bullet lists as-written.
 */
export function extractHireDepartmentsFromProfileText(
  profileText: string,
): Array<{ name: string }> {
  if (!profileText.trim()) return [];
  const found: string[] = [];
  const push = (name: string) => {
    const n = name
      .trim()
      .replace(/\*\*/g, "")
      .replace(/^[-*•]\s+/, "")
      .replace(/\s+/g, " ")
      .replace(/[.:]+$/, "");
    if (n.length < 2 || n.length > 80) return;
    if (
      /^(where|serving|learn more|ministries|departments|overview|service offerings|services|programs|technology|facilities|patient experience|customer experience|client experience|guest experience|member experience|homeowner experience|student(?: & family)? experience|contact|verified from your website)$/i.test(
        n,
      )
    ) {
      return;
    }
    if (found.some((f) => f.toLowerCase() === n.toLowerCase())) return;
    found.push(n);
  };

  const pushBulletsFromSection = (body: string) => {
    for (const line of body.split("\n")) {
      const bullet =
        line.match(/^\s*[-*•]\s+(.+)$/) ||
        line.match(/^\s*\d+[.)]\s+(.+)$/);
      if (bullet?.[1]) push(bullet[1]);
    }
  };

  // Prefer Service Offerings first — this is the Organization Profile source of truth.
  const sectionPatterns = [
    /(?:^|\n)\s*(?:#{1,4}\s*)?(?:\*\*)?service offerings(?:\*\*)?\b[^\n]*\n([\s\S]{0,4000}?)(?=\n\s*#{1,4}\s|\n\s*\*\*[A-Za-z][^*\n]{2,60}\*\*\s*\n|$)/i,
    /(?:^|\n)\s*(?:#{1,4}\s*)?(?:\*\*)?(?:ministries|departments|ministry teams|our ministries|programs|what we offer|services)(?:\*\*)?\b[^\n]*\n([\s\S]{0,4000}?)(?=\n\s*#{1,4}\s|\n\s*\*\*[A-Za-z][^*\n]{2,60}\*\*\s*\n|$)/i,
  ];
  for (const pattern of sectionPatterns) {
    const sectionMatch = profileText.match(pattern);
    if (sectionMatch?.[1]) {
      pushBulletsFromSection(sectionMatch[1]);
      if (found.length >= 2) {
        return found.slice(0, 8).map((name) => ({ name }));
      }
    }
  }

  // Fallback phrase match for shorter church profiles without a Service Offerings heading.
  if (looksChurchOrNonprofitContent(profileText)) {
    const phrases: Array<[RegExp, string]> = [
      [/\bmen'?s(?:\s+and\s+women'?s)?(?:\s+ministr(?:y|ies))?\b/i, "Men's and Women's Ministries"],
      [/\bwomen'?s(?:\s+ministry)?\b/i, "Women's Ministry"],
      [/\bchildren'?s(?:\s+ministry)?\b/i, "Children's Ministry"],
      [/\bstudent'?s?(?:\s+ministry)?\b/i, "Student's Ministry"],
      [/\bsunday\s+worship\b/i, "Sunday Worship Services"],
      [/\badult\s+education\b/i, "Adult Education"],
      [/\bchristian\s+education\b/i, "Christian Education"],
      [/\bsmall\s+groups?\b/i, "Small Groups"],
      [/\bworship(?:\s+ministry)?\b/i, "Worship"],
      [/\bfood\s+bank\b/i, "Food Bank"],
      [/\bprayer(?:\s+list|\s+team)?\b/i, "Prayer"],
      [/\bcare\s*(?:&|and)?\s*benevolence\b/i, "Care & Benevolence"],
      [/\bglobal\s+outreach\b/i, "Global Outreach"],
      [/\byouth(?:\s+(?:group|ministry))?\b/i, "Youth Ministry"],
      [/\bcommunity\s+events\b/i, "Community Events and Special Programs"],
    ];
    for (const [re, label] of phrases) {
      if (re.test(profileText)) push(label);
    }
  }

  return found.slice(0, 8).map((name) => ({ name }));
}

export function defaultHireDepartmentsForVertical(
  vertical: IndustryVertical,
  contentHint?: string,
): Array<{ name: string }> {
  const hint = contentHint ?? "";
  const lower = hint.toLowerCase();
  // Always prefer Organization Profile Service Offerings when the profile lists them.
  const fromProfile = extractHireDepartmentsFromProfileText(hint);
  if (fromProfile.length >= 2) return fromProfile;

  // Church defaults only when the business was classified as a nonprofit.
  // Do not scan the profile for words like "ministry" — that labeled a dental office Men's Ministry.
  if (vertical === "nonprofit") {
    return [
      { name: "Men's Ministry" },
      { name: "Women's Ministry" },
      { name: "Children's Ministry" },
      { name: "Student's Ministry" },
      { name: "Worship" },
      { name: "Care & Prayer" },
    ];
  }

  const isDental = /dental|dentist|orthodont|oral surgery|periodont/.test(lower);
  const isMedical =
    isDental ||
    vertical === "healthcare" ||
    /\b(clinic|medical practice|physician|physical therapy|chiropractic|patient care)\b/.test(
      lower,
    );

  if (isMedical) {
    return [{ name: "Front Desk" }, { name: "Clinical" }];
  }
  if (vertical === "legal") {
    return [{ name: "Intake" }, { name: "Legal Team" }];
  }
  if (vertical === "home_services") {
    return [{ name: "Customer Service" }, { name: "Field Operations" }];
  }

  const isRealEstate =
    vertical === "real_estate" ||
    /\b(real estate|homebuyer|home buyer|we buy houses|cash offer|sell (your )?home|realtor|property)\b/.test(
      lower,
    );
  if (isRealEstate) {
    return [
      { name: "Acquisitions" },
      { name: "Dispositions" },
      { name: "Customer Care" },
      { name: "Operations" },
    ];
  }

  const isEducation =
    vertical === "education" ||
    /\b(school|academy|university|college|prep|enrollment|admissions)\b/.test(lower);
  if (isEducation) {
    return [
      { name: "Admissions" },
      { name: "Academics" },
      { name: "Student Life" },
      { name: "Athletics" },
    ];
  }
  if (
    /\b(mep|mechanical|electrical engineering|plumbing|fire protection|life safety|commissioning|engineering firm|engineering company|built environment)\b/.test(
      lower,
    )
  ) {
    return [
      { name: "Mechanical Engineering" },
      { name: "Electrical Engineering" },
      { name: "Plumbing Engineering" },
      { name: "Fire Protection and Life Safety" },
    ];
  }
  return [{ name: "Customer Service" }, { name: "Operations" }];
}

export function normalizeRecommendationsSelection(
  partial: Partial<HireRecommendationsSelection>,
): HireRecommendationsSelection {
  const locationCount = Math.max(1, partial.locationCount ?? 1);
  const departmentCount = Math.max(1, partial.departmentCount ?? 1);
  const liveChat = partial.liveChat ?? true;
  const textMessages = partial.textMessages ?? false;
  const email = partial.email ?? partial.dedicatedEmail ?? false;
  const browserCalls = partial.browserCalls ?? false;
  const phoneCalls = partial.phoneCalls ?? false;
  const videoCalls = partial.videoCalls ?? false;
  const welcomeVideo = partial.welcomeVideo ?? false;
  const messenger = partial.messenger ?? false;
  const whatsapp = partial.whatsapp ?? false;

  let schedulingIntegrationId = partial.schedulingIntegrationId ?? "none";
  let emrIntegrationEnabled = partial.emrIntegrationEnabled ?? false;
  let emrIntegrationId: MarketplaceEmrIntegrationId = partial.emrIntegrationId ?? "none";

  if (partial.emrIntegrationId === undefined && isEmrIntegrationId(schedulingIntegrationId)) {
    emrIntegrationEnabled = partial.appointmentsEnabled ?? true;
    emrIntegrationId = schedulingIntegrationId;
    schedulingIntegrationId = "none";
  }

  if (
    schedulingIntegrationId !== "none" &&
    isEmrIntegrationId(schedulingIntegrationId)
  ) {
    emrIntegrationEnabled = true;
    emrIntegrationId = schedulingIntegrationId;
    schedulingIntegrationId = "none";
  }

  return {
    liveChat,
    textMessages,
    email,
    browserCalls,
    videoCalls,
    phoneCalls,
    welcomeVideo,
    messenger,
    whatsapp,
    dedicatedEmail: email,
    dedicatedPhone: false,
    appointmentsEnabled: partial.appointmentsEnabled ?? false,
    schedulingIntegrationId,
    emrIntegrationEnabled,
    emrIntegrationId,
    agentTrainingBasic: true,
    agentTrainingAdvanced: partial.agentTrainingAdvanced ?? false,
    chatConversations: liveChat
      ? Math.max(INCLUDED_FREE_CHAT_CONVERSATIONS, partial.chatConversations ?? INCLUDED_FREE_CHAT_CONVERSATIONS)
      : Math.max(0, partial.chatConversations ?? 0),
    smsCount: textMessages
      ? Math.max(INCLUDED_FREE_SMS, partial.smsCount ?? INCLUDED_FREE_SMS)
      : Math.max(0, partial.smsCount ?? 0),
    phoneCallMinutes: phoneCalls
      ? Math.max(INCLUDED_FREE_PHONE_MINUTES, partial.phoneCallMinutes ?? INCLUDED_FREE_PHONE_MINUTES)
      : Math.max(0, partial.phoneCallMinutes ?? 0),
    videoCallMinutes: videoCalls
      ? Math.max(INCLUDED_FREE_VIDEO_MINUTES, partial.videoCallMinutes ?? INCLUDED_FREE_VIDEO_MINUTES)
      : Math.max(0, partial.videoCallMinutes ?? 0),
    welcomeVideoMinutes: welcomeVideo
      ? Math.max(
          INCLUDED_FREE_WELCOME_VIDEO_MINUTES,
          partial.welcomeVideoMinutes ?? INCLUDED_FREE_WELCOME_VIDEO_MINUTES,
        )
      : Math.max(0, partial.welcomeVideoMinutes ?? 0),
    locationCount,
    locationList: partial.locationList ?? [],
    locationDetails: padHireLocationDetails(
      partial.locationDetails?.length
        ? partial.locationDetails.slice(0, locationCount)
        : locationListToDetails(partial.locationList ?? []).slice(0, locationCount),
      locationCount,
      partial.locationList?.[0] ?? "Main Office",
    ),
    departmentCount,
    departmentDetails: padHireDepartmentDetails(
      partial.departmentDetails?.length
        ? partial.departmentDetails.slice(0, departmentCount)
        : Array.from({ length: departmentCount }, (_, index) => ({
            name: partial.departmentDetails?.[index]?.name?.trim() || `Department ${index + 1}`,
            leadTeamMemberEmail: partial.departmentDetails?.[index]?.leadTeamMemberEmail,
            otherTeamMemberEmails: partial.departmentDetails?.[index]?.otherTeamMemberEmails,
          })),
      departmentCount,
    ),
    teamMemberCount: Math.max(1, partial.teamMemberCount ?? 1),
    extraTeamMembers: partial.extraTeamMembers ?? [],
    teamMembersLabel:
      partial.teamMembersLabel ??
      (locationCount <= 1
        ? "Lead Team Member + Account Owner"
        : `${locationCount} Lead Team Members (one per location) + Account Owner`),
    teamHandoffEnabled: partial.teamHandoffEnabled ?? true,
    teamHandoffCtaClusters: normalizeHireTeamHandoffCtaClusters(
      partial.teamHandoffCtaClusters,
      DEFAULT_TEAM_HANDOFF_CTAS,
    ),
  };
}

/** Reset paid add-ons so monthly total returns to the free Live Chat tier. */
export function resetRecommendationsToFreeTier(
  selection: HireRecommendationsSelection,
): HireRecommendationsSelection {
  return normalizeRecommendationsSelection({
    ...selection,
    liveChat: true,
    textMessages: false,
    email: false,
    browserCalls: false,
    videoCalls: false,
    phoneCalls: false,
    welcomeVideo: false,
    messenger: false,
    whatsapp: false,
    dedicatedEmail: false,
    dedicatedPhone: false,
    appointmentsEnabled: false,
    schedulingIntegrationId: "none",
    emrIntegrationEnabled: false,
    emrIntegrationId: "none",
    agentTrainingBasic: true,
    agentTrainingAdvanced: false,
    chatConversations: INCLUDED_FREE_CHAT_CONVERSATIONS,
    smsCount: 0,
    phoneCallMinutes: 0,
    videoCallMinutes: 0,
    welcomeVideoMinutes: 0,
    extraTeamMembers: [],
    teamMemberCount: 1,
    locationCount: 1,
    departmentCount: 1,
    departmentDetails: selection.departmentDetails.slice(0, 1),
    locationList: selection.locationList.slice(0, 1),
    locationDetails: selection.locationDetails.slice(0, 1),
    teamHandoffEnabled: true,
    teamHandoffCtaClusters: selection.teamHandoffCtaClusters.slice(0, 2),
  });
}

export function writeHireRecommendationsSelection(
  selection: HireRecommendationsSelection,
): void {
  if (typeof window === "undefined") return;
  try {
    const raw = JSON.stringify(normalizeRecommendationsSelection(selection));
    sessionStorage.setItem(HIRE_RECOMMENDATIONS_SESSION_KEY, raw);
    localStorage.setItem(HIRE_RECOMMENDATIONS_SESSION_KEY, raw);
  } catch {
    /* ignore */
  }
}

export function readHireRecommendationsSelection(): HireRecommendationsSelection | null {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      sessionStorage.getItem(HIRE_RECOMMENDATIONS_SESSION_KEY) ||
      localStorage.getItem(HIRE_RECOMMENDATIONS_SESSION_KEY);
    if (!raw) return null;
    return normalizeRecommendationsSelection(JSON.parse(raw) as Partial<HireRecommendationsSelection>);
  } catch {
    return null;
  }
}

export const RECOMMENDATIONS_APPOINTMENTS_OPTIONS: {
  id: Exclude<MarketplaceAppointmentsIntegrationId, "none">;
  label: string;
}[] = [
  { id: "google_calendar", label: "Google Calendar" },
  { id: "calendly", label: "Calendly" },
  { id: "zoom", label: "Zoom" },
];

export const RECOMMENDATIONS_EMR_OPTIONS: {
  id: Exclude<MarketplaceEmrIntegrationId, "none">;
  label: string;
}[] = [
  { id: "juvonno", label: "Juvonno" },
  { id: "dentrix", label: "Dentrix" },
  { id: "dentrix_ascend", label: "Dentrix Ascend" },
  { id: "open_dental", label: "Open Dental" },
  { id: "eaglesoft", label: "Eaglesoft" },
  { id: "curve", label: "Curve" },
  { id: "denticon", label: "Denticon" },
];

/** @deprecated Use RECOMMENDATIONS_APPOINTMENTS_OPTIONS or RECOMMENDATIONS_EMR_OPTIONS. */
export const RECOMMENDATIONS_SCHEDULING_OPTIONS: {
  id: MarketplaceSchedulingIntegrationId;
  label: string;
}[] = [
  ...RECOMMENDATIONS_APPOINTMENTS_OPTIONS,
  ...RECOMMENDATIONS_EMR_OPTIONS,
];

/** Product name only (no unit price) — use in SiteGlue connect UI / post-connect states. */
export function recommendationsSchedulingProductLabel(
  id: MarketplaceSchedulingIntegrationId,
): string {
  const option =
    RECOMMENDATIONS_APPOINTMENTS_OPTIONS.find((o) => o.id === id) ??
    RECOMMENDATIONS_EMR_OPTIONS.find((o) => o.id === id);
  return option?.label ?? "None";
}

export function formatRecommendationsSchedulingLabel(
  id: MarketplaceSchedulingIntegrationId,
  options?: { hidePrice?: boolean },
): string {
  const option =
    RECOMMENDATIONS_APPOINTMENTS_OPTIONS.find((o) => o.id === id) ??
    RECOMMENDATIONS_EMR_OPTIONS.find((o) => o.id === id);
  if (!option) return "None";
  if (options?.hidePrice) return option.label;
  const price = isEmrIntegrationId(id)
    ? emrIntegrationPriceCents(id)
    : schedulingIntegrationPriceCents(id);
  return `${option.label} ${formatHirePrice(price)}/mo`;
}

export function formatRecommendationsEmrLabel(
  id: MarketplaceEmrIntegrationId,
  options?: { hidePrice?: boolean },
): string {
  if (id === "none") return "None";
  const option = RECOMMENDATIONS_EMR_OPTIONS.find((o) => o.id === id);
  if (!option) return "None";
  if (options?.hidePrice) return option.label;
  return `${option.label} ${formatHirePrice(emrIntegrationPriceCents(id))}/mo`;
}

export type MarketplaceAgentPricingInput = {
  channels: Record<string, boolean>;
  videoCallEnabled: boolean;
  skills: string[];
  company: {
    locations: { name: string; address?: string; city?: string; state?: string; postalCode?: string; phone?: string; businessHours?: string }[];
  };
  training: {
    ragItems: unknown[];
  };
};

const SKILL_EMR_ALIASES: { pattern: RegExp; id: Exclude<MarketplaceEmrIntegrationId, "none"> }[] = [
  { pattern: /juvonno|nexhealth/i, id: "juvonno" },
  { pattern: /dentrix ascend/i, id: "dentrix_ascend" },
  { pattern: /dentrix/i, id: "dentrix" },
  { pattern: /open dental/i, id: "open_dental" },
  { pattern: /eaglesoft/i, id: "eaglesoft" },
  { pattern: /\bcurve\b/i, id: "curve" },
  { pattern: /denticon/i, id: "denticon" },
];

function detectEmrFromSkills(skills: string[]): Exclude<MarketplaceEmrIntegrationId, "none"> | null {
  const haystack = skills.join(" ");
  for (const entry of SKILL_EMR_ALIASES) {
    if (entry.pattern.test(haystack)) return entry.id;
  }
  return null;
}

function detectSchedulingFromSkills(
  skills: string[],
): Exclude<MarketplaceAppointmentsIntegrationId, "none"> | null {
  const haystack = skills.join(" ").toLowerCase();
  if (/calendly/.test(haystack)) return "calendly";
  if (/google calendar/.test(haystack)) return "google_calendar";
  if (/\bzoom\b/.test(haystack)) return "zoom";
  if (/appointment|schedul|booking/.test(haystack)) return "google_calendar";
  return null;
}

/** Derive compensation calculator defaults from a marketplace template agent profile. */
export function recommendationsFromMarketplaceAgent(
  agent: MarketplaceAgentPricingInput,
): HireRecommendationsSelection {
  const locationList = agent.company.locations
    .map((loc) => loc.name.trim())
    .filter(Boolean);
  const locationCount = Math.max(1, locationList.length || 1);
  const liveChat = agent.channels.aiChat === true;
  const phoneCalls = agent.channels.call === true;
  const videoCalls = agent.videoCallEnabled && agent.channels.talk === true;
  const textMessages = agent.channels.sms === true;
  const email = agent.channels.email === true;
  const messenger = agent.channels.messenger === true;
  const whatsapp = agent.channels.whatsapp === true;
  const browserCalls = agent.channels.talk === true;
  const schedulingId = detectSchedulingFromSkills(agent.skills);
  const emrId = detectEmrFromSkills(agent.skills);

  return normalizeRecommendationsSelection({
    liveChat,
    textMessages,
    email,
    browserCalls,
    phoneCalls,
    videoCalls,
    messenger,
    whatsapp,
    appointmentsEnabled: schedulingId != null,
    schedulingIntegrationId: schedulingId ?? "none",
    emrIntegrationEnabled: emrId != null,
    emrIntegrationId: emrId ?? "none",
    agentTrainingAdvanced: agent.training.ragItems.length > 0,
    chatConversations: liveChat ? INCLUDED_FREE_CHAT_CONVERSATIONS : 0,
    smsCount: textMessages ? INCLUDED_FREE_SMS : 0,
    phoneCallMinutes: phoneCalls ? INCLUDED_FREE_PHONE_MINUTES : 0,
    videoCallMinutes: videoCalls ? INCLUDED_FREE_VIDEO_MINUTES : 0,
    welcomeVideoMinutes: 0,
    locationCount,
    locationList,
    locationDetails: agent.company.locations.map((loc) => ({
      name: loc.name.trim(),
      address: loc.address?.trim() || undefined,
      city: loc.city?.trim() || undefined,
      state: loc.state?.trim() || undefined,
      postalCode: loc.postalCode?.trim() || undefined,
      phone: loc.phone?.trim() || undefined,
      businessHours: loc.businessHours?.trim() || undefined,
    })),
    departmentCount: 1,
    departmentDetails: [{ name: "General" }],
    teamMemberCount: 1,
    teamMembersLabel:
      locationCount <= 1
        ? "Lead Team Member + Account Owner"
        : `${locationCount} Lead Team Members (one per location) + Account Owner`,
  });
}
